import { useEffect, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import styled from 'styled-components'
import { useForm } from 'react-hook-form'
import { useMutation } from '@tanstack/react-query'

import apiService from 'services/api'
import { INote } from 'models/contact_companies'
import { MobileColors } from 'styles/mobileTokens'
import { FbLink } from 'components/FbUI/FbLink'
import { NoteManager } from 'components/Notes/NoteManager'
import { toast } from 'react-toastify'
import { useQueryClient } from '@tanstack/react-query'

type LocationState = {
  note: INote
  companyName?: string | null
  doorName?: string | null
  contactCompanyId?: string | null
  doorProxyId?: string | null
}

type FormValues = {
  note: string
  attachments: File[]
}

const Page = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 85vh;
  background: #f3f4f6;
`

const HeaderSection = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid ${MobileColors.borderDefault};
`

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  margin: 0 0 8px 0;
  color: #191d23;
`

const CompanyLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
`

const Content = styled.div`
  flex: 1;
  padding-bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const EditorCard = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  // min-height: 40vh;
  background: #f3f4f6;
`

const EditorArea = styled.div`
  flex: 1;
  overflow-y: auto;
  background: #ffffff;
  border: 1px solid ${MobileColors.borderDefault};
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.08);
  display: flex;
  flex-direction: column;
  padding: 16px;

  .ql-container {
    border: none;
    font-size: 16px;
  }

  .ql-editor {
    padding: 12px;
    min-height: 30vh;
  }

  .ql-editor.ql-blank::before {
    color: ${MobileColors.textSecondary};
    font-style: normal;
  }

  .ql-toolbar {
    background: #ffffff;
    border: none;
    border-top: 1px solid ${MobileColors.borderDefault};
    padding: 8px 12px;
  }
`

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  padding-inline: 16px;
  flex-shrink: 0;
`

const CancelButton = styled.button`
  flex: 1;
  padding: 14px 24px;
  border: 1px solid ${MobileColors.borderDefault};
  border-radius: 8px;
  background: white;
  font-size: 16px;
  font-weight: 500;
  color: ${MobileColors.textPrimary};
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const SaveButton = styled.button`
  flex: 1.5;
  padding: 14px 24px;
  border: none;
  border-radius: 8px;
  background: #2c6b4e;
  font-size: 16px;
  font-weight: 600;
  color: white;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #245a42;
  }

  &:disabled {
    background: #2c6b4e;
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const DeleteNoteButton = styled.button`
  margin-bottom: 8px;
  align-self: center;
  background: none;
  border: none;
  padding: 0;
  font-size: 11px;
  color: #b91c1c;
  text-decoration: underline;
  cursor: pointer;
  flex-shrink: 0;
`

export function MobileEditNotePage() {
  const navigate = useNavigate()
  const { state } = useLocation() as { state: LocationState | null }
  const originalNote = state?.note
  const [searchParams] = useSearchParams()
  const api = apiService()
  const queryClient = useQueryClient()

  if (!originalNote) {
    navigate(-1)
    return null
  }

  const contactCompanyIdParam = searchParams.get('contact_company_id')
  const doorProxyIdParam = searchParams.get('door_proxy_id')
  const companyNameParam = searchParams.get('company_name')
  const doorNameParam = searchParams.get('door_name')

  const companyDisplay =
    companyNameParam || (originalNote as any).company_name || ''
  const rawDoorDisplay = doorNameParam || (originalNote as any).door_name || ''

  const contactCompanyId =
    contactCompanyIdParam || (originalNote as any).contact_company_id
  const doorProxyId = doorProxyIdParam || (originalNote as any).door_proxy_id

  const hasDoor = !!rawDoorDisplay && !!doorProxyId
  const doorDisplay = hasDoor ? rawDoorDisplay : 'Insert door info'

  const { control, handleSubmit, reset } = useForm<FormValues>({
    defaultValues: {
      note: originalNote.content || '',
      attachments: [],
    },
  })

  const [attachmentsToDelete, setAttachmentsToDelete] = useState<number[]>([])

  useEffect(() => {
    return () => {
      setAttachmentsToDelete([])
      reset()
    }
  }, [reset])

  const mutation = useMutation<void, Error, FormValues>({
    mutationFn: async (values) => {
      if (attachmentsToDelete.length > 0) {
        await Promise.all(
          attachmentsToDelete.map((attachmentId) =>
            api.deleteNoteAttachment(originalNote.id, attachmentId)
          )
        )
      }

      await api.editNote(originalNote.id, values.note)

      if (values.attachments.length > 0) {
        await api.addAttachmentsToNote(originalNote.id, values.attachments)
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['notes-infinite'] })
      await queryClient.refetchQueries({ queryKey: ['notes-infinite'] })
      toast.success('Note updated successfully')
      navigate(-1)
    },
  })

  const isSubmitting = mutation.isPending

  const onSubmit = handleSubmit(async (values) => {
    await mutation.mutateAsync(values)
  })

  const handleCancel = () => {
    navigate(-1)
  }

  const handleDelete = () => {
    navigate(`/notes/${originalNote.id}/delete`, {
      state: { note: originalNote },
    })
  }

  return (
    <Page>
      <HeaderSection>
        <PageTitle>Edit Note</PageTitle>

        {(companyDisplay || doorDisplay) && (
          <CompanyLinks>
            {companyDisplay && contactCompanyId && (
              <FbLink to={`/company/${contactCompanyId}`}>
                {companyDisplay}
              </FbLink>
            )}

            {companyDisplay && <span>|</span>}

            {hasDoor ? (
              <FbLink to={`/door/${doorProxyId}`}>{doorDisplay}</FbLink>
            ) : (
              <span className="text-gray-400 italic">{doorDisplay}</span>
            )}
          </CompanyLinks>
        )}
      </HeaderSection>

      <Content>
        <EditorCard>
          <EditorArea>
            <NoteManager<FormValues>
              control={control}
              noteName="note"
              attachmentsName="attachments"
              label=""
              placeholder="Start writing note here"
              existingNote={originalNote}
              viewMode={false}
              editorClassName="h-[30vh]"
              enableMentions
              onAttachmentsToDelete={setAttachmentsToDelete}
            />
          </EditorArea>
        </EditorCard>

        <ButtonContainer>
          <CancelButton onClick={handleCancel} disabled={isSubmitting}>
            Cancel
          </CancelButton>
          <SaveButton onClick={onSubmit} disabled={isSubmitting}>
            Save
          </SaveButton>
        </ButtonContainer>

        <DeleteNoteButton
          type="button"
          onClick={handleDelete}
          disabled={isSubmitting}
        >
          Delete Note
        </DeleteNoteButton>
      </Content>
    </Page>
  )
}
